import { useMsal } from '@azure/msal-react';
import {
  useState, createContext, useMemo, Dispatch, SetStateAction, useContext, PropsWithChildren,
} from 'react';
import { useIsMsalAuth } from '../..';
import { getFirstLastName } from '../../../../utils/string';

const defaultUser = null;

const defaultUserContext = {
  user: defaultUser,
  setUser: () => { },
  deleteUser: () => { },
};

// eslint-disable-next-line no-shadow
export enum UserRoles {
  BROKER_ADMIN = 'BROKER_ADMIN',
  BROKER_USER = 'BROKER_USER',
  COMPLIANCE = 'COMPLIANCE',
  BDM = 'BDM',
  GUEST = 'GUEST',
  // Internal Roles
  UNDERWRITER = 'UNDERWRITER',
  READ_ONLY = 'READ_ONLY',
  SALES_SUPPORT = 'SALES_SUPPORT',
  CAN_CHANGE_APPLICATION_STATUS = 'CAN_CHANGE_APPLICATION_STATUS',
  CAN_APPROVE_PRODUCT_OVERRIDES = 'CAN_APPROVE_PRODUCT_OVERRIDES',
  CAN_APPROVE_INCOME_OVERRIDES = 'CAN_APPROVE_INCOME_OVERRIDES',
  CAN_APPROVE_POLICY_SHIELD_OVERRIDES = 'CAN_APPROVE_POLICY_SHIELD_OVERRIDES',
  CAN_OFFER_CASE = 'CAN_OFFER_CASE',
  CAN_MANAGE_BROKER_USERS = 'CAN_MANAGE_BROKER_USERS',
  CAN_REGENERATE_API_SECRET = 'CAN_REGENERATE_API_SECRET',
}

type IdTokenClaims = {
  roles: UserRoles[]
};

export type UserType = {
  id: string,
  // External User Role will be an array/string.
  // Internal User Role will always be an array.
  role?: UserRoles | UserRoles[],
  firstName: string,
  lastName: string,
  email: string
  companyTradingName?: string,
  brokerUserId?: string,
  companyId?: string,
} | null

type UserContextType = {
  user: UserType,
  setUser: Dispatch<SetStateAction<UserType>>,
  deleteUser: () => void,
}

export const UserContext = createContext<UserContextType>(defaultUserContext);

export function UserProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<UserType | null>(defaultUser);

  const deleteUser = () => setUser(null);

  const userValue = useMemo(() => ({
    user, setUser, deleteUser,
  }), [user]);

  return (
    <UserContext.Provider value={userValue}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}

export function useCombinedUser(): UserContextType {
  const userContext = useUser();
  const isMsalAuth = useIsMsalAuth();
  const { accounts } = useMsal();

  if (userContext.user || !isMsalAuth) return userContext;

  const account = accounts[0];
  const { firstName, lastName } = getFirstLastName(account?.name);

  return {
    ...userContext,
    user: {
      id: account.localAccountId,
      firstName,
      lastName,
      role: (account?.idTokenClaims as IdTokenClaims)?.roles,
      email: account?.username ?? '',
    },
  };
}

export function isUserWithRoles(roles: UserRoles[], user: UserType) {
  if (!user) return false;
  if (Array.isArray(user.role)) return !!user.role.find((role) => roles.includes(role));
  return roles.includes(user.role!);
}

export function useUserHasRole(role: keyof typeof UserRoles) {
  const { user } = useCombinedUser();
  return !!user && [user?.role].flat().includes(UserRoles[role]);
}

export function useUserHasRoles(roles: (keyof typeof UserRoles)[]) {
  const { user } = useCombinedUser();
  const userRoles = [user?.role].flat();
  return !!user && roles.every((role) => userRoles.includes(UserRoles[role]));
}

export function useUserHasOneOfRoles(roles: (keyof typeof UserRoles)[]) {
  const { user } = useCombinedUser();
  const userRoles = [user?.role].flat();
  return !!user && !!roles.find((role) => userRoles.includes(UserRoles[role]));
}
